
import React, { createContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { useUI } from './UIContext';
import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';
import { Usuario, Socio, Producto, ReglaPuntos, SesionCaja, Venta, MovimientoPunto, Rol, ClubConteoSocios, Plan, Club, PlanSuscripcion, AuditEvento, Cancha, Turno, ParticipanteInput, Deuda, DeudaInput, MedioDePago } from '../types';

// --- SUPABASE SETUP ---
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
        'Faltan variables de entorno VITE_SUPABASE_URL y/o VITE_SUPABASE_ANON_KEY. ' +
        'En desarrollo: configurarlas en .env.local (ver .env.example). ' +
        'En Vercel: configurarlas en Project Settings → Environment Variables.'
    );
}

export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;
export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey);
// --- END SUPABASE SETUP ---

type Screen = 'dashboard' | 'socios' | 'productos' | 'caja' | 'deudas' | 'reportes' | 'reglas' | 'configuracion' | 'suscripcion' | 'turnos' | 'perfil' | 'usuarios';
type UnauthenticatedView = 'landing' | 'login' | 'onboarding' | 'login-socio';

interface AppContextType {
  user: Usuario | null;
  club: Club | null;
  supabase: SupabaseClient;
  loading: boolean;
  error: string | null;
  loginError: string | null;
  registrationError: string | null;
    registerAndCreateClub: (adminName: string, email: string, password: string, clubName: string) => Promise<boolean>;
    registerSocio: (nombre: string, email: string, password: string, clubId?: number) => Promise<boolean>;
  isReadOnly: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  socios: Socio[];
  addSocio: (socio: Omit<Socio, 'id' | 'creado_por' | 'creado_en' | 'puntos' | 'club_id'>) => Promise<Socio | null>;
  bulkAddSocios: (socios: Array<Pick<Socio, 'nombre' | 'apellido' | 'telefono' | 'email' | 'notas'>>) => Promise<{ inserted: number; failed: number; error?: string }>;
  updateSocio: (socio: Socio) => Promise<boolean>;
  deleteSocio: (id: number) => Promise<{ success: boolean; message?: string }>;
  hardDeleteSocio: (id: number) => Promise<{ success: boolean; message?: string }>;
  productos: Producto[];
  addProducto: (producto: Omit<Producto, 'id' | 'club_id'>) => Promise<Producto | null>;
  updateProducto: (producto: Producto) => Promise<boolean>;
  deleteProducto: (id: number) => Promise<{ success: boolean; message?: string }>;
  sesionCaja: SesionCaja | null;
  openCaja: (montoInicial: number) => Promise<void>;
  closeCaja: (montoFinal: number) => Promise<void>;
  registrarVenta: (venta: Omit<Venta, 'id' | 'creado_por' | 'creado_en' | 'club_id'>) => Promise<boolean>;
    // Permite abrir la pantalla de Caja y expandir una venta específica
    pendingOpenVentaId?: number | null;
    openVentaInCaja: (ventaId: number) => void;
    clearPendingOpenVentaId: () => void;
    // Análogo para abrir un turno desde Caja
    pendingOpenTurnoId?: number | null;
    openTurnoInTurnos: (turnoId: number) => void;
    clearPendingOpenTurnoId: () => void;
  reglasPuntos: ReglaPuntos[];
  addReglaPunto: (regla: Omit<ReglaPuntos, 'id' | 'club_id'>) => Promise<boolean>;
  updateReglaPunto: (regla: ReglaPuntos) => Promise<boolean>;
  deleteReglaPunto: (id: number) => Promise<{ success: boolean; message?: string }>;
  movimientosPuntos: MovimientoPunto[];
  // Deudas
  deudas: Deuda[];
  addDeudas: (deudas: DeudaInput[]) => Promise<{ success: boolean; created?: Deuda[]; message?: string }>;
  cobrarDeudas: (ids: number[], medioDePago: MedioDePago) => Promise<{ success: boolean; message?: string }>;
  cancelarDeuda: (id: number) => Promise<{ success: boolean; message?: string }>;
  activeScreen: Screen;
  setActiveScreen: (screen: Screen) => void;
  unauthenticatedView: UnauthenticatedView;
  setUnauthenticatedView: (view: UnauthenticatedView) => void;
  usuarios: Usuario[];
  // Gestión de usuarios staff (solo Admin)
  createStaffUser: (data: { nombre: string; email: string; rol: 'Cajero' | 'Viewer' }) => Promise<{ success: boolean; user?: Usuario; message?: string }>;
  updateStaffUser: (id: string, patch: { nombre?: string; rol?: 'Cajero' | 'Viewer' }) => Promise<{ success: boolean; user?: Usuario; message?: string }>;
  deleteStaffUser: (id: string) => Promise<{ success: boolean; message?: string }>;
  clubes: ClubConteoSocios[];
  updateClubPlan: (clubId: number, plan: Plan) => Promise<void>;
  planes: PlanSuscripcion[];
  addPlan: (planData: Omit<PlanSuscripcion, 'id' | 'creado_en'>) => Promise<boolean>;
  updatePlan: (planData: PlanSuscripcion) => Promise<boolean>;
  auditLogs: AuditEvento[];
  facturas: AuditEvento[];
  paymentSuccessMessage: string | null;
  setPaymentSuccessMessage: (message: string | null) => void;
    clearError: () => void;
    fetchClubData: (clubId: number) => Promise<void>;
  
  // Turnos
  canchas: Cancha[];
  addCancha: (cancha: Omit<Cancha, 'id' | 'club_id'>) => Promise<boolean>;
    updateCancha: (cancha: Cancha) => Promise<boolean>;
  turnos: Turno[];
  addTurno: (turnoData: Omit<Turno, 'id' | 'creado_por' | 'creado_en' | 'club_id'>, participantes: ParticipanteInput[]) => Promise<Turno | null>;
  updateTurno: (turnoId: number, turnoData: Partial<Turno>, participantes: ParticipanteInput[]) => Promise<boolean>;
    updateTurnoRecurrencia?: (turnoId: number, turnoData: Partial<Turno>, participantes: ParticipanteInput[], applyToFuture: boolean) => Promise<boolean>;
    addTurnoRecurrencia?: (recData: any, participantes: ParticipanteInput[]) => Promise<boolean>;
    updateTurnoRecurrenciaRecord?: (recId: number, recData: any) => Promise<boolean>;
    deleteTurnoRecurrencia?: (recId: number) => Promise<boolean>;
  updateTurnoEstado: (turnoId: number, estado: Turno['estado']) => Promise<boolean>;
  deleteTurno: (turnoId: number) => Promise<boolean>;
    updateUserProfile: (payload: { nombre?: string; email?: string; avatar_url?: string }) => Promise<boolean>;
}

export const AppContext = createContext<AppContextType>({} as AppContextType);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Usuario | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [activeScreen, setActiveScreen] = useState<Screen>('dashboard');
  const [unauthenticatedView, setUnauthenticatedView] = useState<UnauthenticatedView>('landing');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState<string | null>(null);
  
  const [socios, setSocios] = useState<Socio[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [sesionCaja, setSesionCaja] = useState<SesionCaja | null>(null);
  const [reglasPuntos, setReglasPuntos] = useState<ReglaPuntos[]>([]);
  const [movimientosPuntos, setMovimientosPuntos] = useState<MovimientoPunto[]>([]);
  const [deudas, setDeudas] = useState<Deuda[]>([]);
  const [clubes, setClubes] = useState<ClubConteoSocios[]>([]);
  const [planes, setPlanes] = useState<PlanSuscripcion[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditEvento[]>([]);
  const [facturas, setFacturas] = useState<AuditEvento[]>([]);
  const [canchas, setCanchas] = useState<Cancha[]>([]);
  const [turnos, setTurnos] = useState<Turno[]>([]);
    const [pendingOpenVentaId, setPendingOpenVentaId] = useState<number | null>(null);
    const [pendingOpenTurnoId, setPendingOpenTurnoId] = useState<number | null>(null);
    const ui = useUI();
    // Diagnostic logging: helps identify if UI context is available in production
    // and whether methods like showModal are present (helps debug React error #310).
    // This will be visible in browser console logs after deployment.
    try {
        console.log('AppProvider init', { uiPresent: !!ui, hasShowModal: !!(ui && (ui as any).showModal) });
        // Also log the typeof to detect if something exported incorrectly (object vs function)
        console.log('AppProvider ui types', { uiType: typeof ui, showModalType: ui && (ui as any).showModal ? typeof (ui as any).showModal : 'undefined' });
    } catch (e) {
        console.warn('AppProvider debug logging failed', e);
    }
  
  const getErrorMessage = (err: any, defaultMessage: string = 'Ocurrió un error inesperado.') => {
    let message = defaultMessage;
    
    if (err) {
        if (typeof err === 'string') {
            message = err;
        } else if (err instanceof Error) {
            message = err.message;
        } else if (typeof err === 'object') {
            if (err.message) {
                message = String(err.message);
            } else if (err.error_description) {
                message = err.error_description;
            } else if (err.details) {
                message = err.details;
            } else if (err.hint) {
                message = err.hint;
            } else if (err.code) {
                message = `Error de base de datos (Código: ${err.code})`;
            } else {
                try {
                    const json = JSON.stringify(err);
                    if (json !== '{}') message = `Error técnico: ${json}`;
                } catch (e) { }
            }
        }
    }

    if (message === '[object Object]') {
         message = 'Se produjo un error desconocido (Objeto de error inválido).';
    }

    if (message.includes('Failed to fetch')) {
        return 'Error de conexión. Verificá que la URL y la Clave de Supabase sean correctas.';
    }
    
    // Traducciones específicas de mensajes comunes de Supabase/Auth
    if (message.includes('Invalid login credentials') || message.toLowerCase().includes('invalid login')) {
        return 'Credenciales inválidas. Verificá tu email y contraseña e intentá nuevamente.';
    }
    
    if (message.includes('violates row-level security policy')) {
        return `Error de permisos: La base de datos bloqueó la operación. (${message})`;
    }

    if (message.includes('User already registered')) {
        return 'El usuario ya está registrado.';
    }

    return message;
  }

  const fetchClubData = async (clubId: number) => {
    try {
        const { data: clubData, error: clubError } = await supabase
            .from('clubes')
            .select('*')
            .eq('id', clubId)
            .single();
        
        if (clubError) throw clubError;
        setClub(clubData);
        
        if (clubData.suscripcion_estado === 'past_due' || clubData.suscripcion_estado === 'canceled') {
            setIsReadOnly(true);
        } else {
            setIsReadOnly(false);
        }

        const { data: profilesData } = await supabase.from('profiles').select('id, nombre, rol, email').eq('club_id', clubId);
        setUsuarios(profilesData as Usuario[] || []);

        // --- CARGA DE DATOS CORE (Esenciales) ---
        // Separamos esto de los "Turnos" para que si las tablas nuevas no existen, la app no explote.
        const [sociosRes, productosRes, reglasRes, movimientosRes, sesionRes, facturasRes, deudasRes] = await Promise.all([
            supabase.from('socios').select('*').eq('club_id', clubId).order('nombre'),
            supabase.from('productos').select('*').eq('club_id', clubId).order('nombre'),
            supabase.from('reglas_puntos').select('*').eq('club_id', clubId),
            supabase.from('movimientos_puntos').select('*').eq('club_id', clubId),
            supabase.from('sesiones_caja').select('*, ventas(*, socio:socios(*), items:venta_items(*, producto:productos(*)))').eq('club_id', clubId).order('abierta_en', { ascending: false }).limit(1),
            supabase.from('auditoria_eventos').select('*').eq('club_id', clubId).eq('tipo_evento', 'PAGO_EXITOSO').order('creado_en', { ascending: false }),
            supabase.from('deudas').select('*, socio:socios(*)').eq('club_id', clubId).order('creado_en', { ascending: false }),
        ]);
        
        if (sociosRes.error) throw sociosRes.error; // Si falla algo core, lanzamos error general.

        // Debug logging: mostrar conteos de datos recibidos para ayudar a diagnosticar cargas vacías
        try {
            // Log current session to inspect token and claims (helpful for RLS debugging)
            try {
                const _session = await supabase.auth.getSession();
                console.log('fetchClubData: session ->', _session);
            } catch(e) {
                console.warn('fetchClubData: could not read session', e);
            }

            console.info('fetchClubData: resultados ->', {
                socios: Array.isArray(sociosRes.data) ? sociosRes.data.length : sociosRes.data,
                productos: Array.isArray(productosRes.data) ? productosRes.data.length : productosRes.data,
                reglas: Array.isArray(reglasRes.data) ? reglasRes.data.length : reglasRes.data,
                movimientos: Array.isArray(movimientosRes.data) ? movimientosRes.data.length : movimientosRes.data,
                sesiones: sesionRes.data ? (Array.isArray(sesionRes.data) ? sesionRes.data.length : 1) : 0,
                facturas: Array.isArray(facturasRes.data) ? facturasRes.data.length : facturasRes.data
            });

            // Log full Supabase response objects for deeper inspection (data, error, status)
            console.log('fetchClubData: sociosRes ->', sociosRes);
            console.log('fetchClubData: productosRes ->', productosRes);
            console.log('fetchClubData: reglasRes ->', reglasRes);
            console.log('fetchClubData: movimientosRes ->', movimientosRes);
            console.log('fetchClubData: sesionRes ->', sesionRes);
            console.log('fetchClubData: facturasRes ->', facturasRes);

            // Also log the profiles/users load we did earlier to ensure club membership is present
            console.log('fetchClubData: usuarios cargados ->', profilesData);
        } catch(e) { console.warn('fetchClubData debug log failed', e); }

        // Sanitize responses to plain objects to avoid getters/proxies from causing runtime errors
        const sanitize = (v: any) => {
            if (v === null || v === undefined) return v;
            try {
                return JSON.parse(JSON.stringify(v));
            } catch (e) {
                return v;
            }
        };
        const safeArr = (a: any) => Array.isArray(a) ? a.map(sanitize) : [];

        const sociosArr = safeArr(sociosRes.data);
        setSocios(sociosArr);
        setProductos(safeArr(productosRes.data));
        setReglasPuntos(safeArr(reglasRes.data));
        setMovimientosPuntos(safeArr(movimientosRes.data));
        setFacturas(safeArr(facturasRes.data));
        // Deudas (tolerante a fallo si la tabla aún no fue migrada en algún ambiente)
        if (deudasRes && !deudasRes.error) {
            setDeudas(safeArr(deudasRes.data));
        } else if (deudasRes?.error) {
            console.warn('fetchClubData: no se pudo cargar deudas', deudasRes.error);
            setDeudas([]);
        }
        
        if (sesionRes.data && sesionRes.data.length > 0) {
            setSesionCaja(sesionRes.data[0] as unknown as SesionCaja);
        } else {
            setSesionCaja(null);
        }

        // --- CARGA DE MÓDULO TURNOS (Opcional/Seguro) ---
        // Intentamos cargar turnos. Si falla (ej. tabla no existe), loggeamos pero no rompemos la app.
        try {
            const [canchasRes, turnosRes] = await Promise.all([
                supabase.from('canchas').select('*').eq('club_id', clubId).order('nombre'),
                supabase.from('turnos').select('*, cancha:canchas(*), socio_titular:socios(*), participantes:turno_participantes(*, socio:socios(*))').eq('club_id', clubId).order('fecha_inicio', { ascending: false }).limit(100)
            ]);

            // Verificamos errores explícitamente para lanzarlos al catch local
            if (canchasRes.error) throw canchasRes.error;
            if (turnosRes.error) throw turnosRes.error;

            setCanchas(canchasRes.data || []);
            let baseTurnos = (turnosRes.data || []) as unknown as Turno[];

            // Hidratar participantes por separado por si el join no devuelve datos
            try {
                const turnoIds = baseTurnos.map(t => t.id).filter(id => id !== null && id !== undefined);
                if (turnoIds.length > 0) {
                    const { data: partsData, error: partsErr } = await supabase
                        .from('turno_participantes')
                        .select('*, socio:socios(*)')
                        .in('turno_id', turnoIds);

                    if (!partsErr && partsData) {
                        console.log('fetchClubData: turno_participantes ->', partsData);
                        const map = new Map<number, any[]>();
                        for (const p of partsData as any[]) {
                            const tid = Number(p.turno_id);
                            if (!map.has(tid)) map.set(tid, []);
                            map.get(tid)!.push(p);
                        }
                        baseTurnos = baseTurnos.map(t => ({
                            ...t,
                            participantes: map.get(Number(t.id)) || t.participantes || []
                        }));
                    } else {
                        console.warn('fetchClubData: sin participantes o error ->', partsErr);
                    }
                }
            } catch (e) {
                console.warn('No se pudieron hidratar participantes de turnos:', e);
            }

            setTurnos(baseTurnos);

            // Cargar recurrencias (si la tabla existe). Expandir ocurrencias en rango razonable
                try {
                    const recRes = await supabase.from('turnos_recurrentes').select('*').eq('club_id', clubId).eq('activo', true);
                    const excRes = await supabase.from('turnos_recurrentes_excepciones').select('*').eq('club_id', clubId);
                    const excepciones = (!excRes.error && excRes.data) ? excRes.data as any[] : [];

                    if (!recRes.error && recRes.data && recRes.data.length > 0) {
                        const recurrencias = recRes.data as any[];
                        // generar ocurrencias desde 30 días en el pasado hasta 365 en el futuro
                        const startRange = new Date(); startRange.setDate(startRange.getDate() - 30);
                        const endRange = new Date(); endRange.setDate(endRange.getDate() + 365);

                        const existingStarts = new Set<string>(baseTurnos.map(t => String(t.fecha_inicio)));
                        const generated: Turno[] = [];

                        for (const r of recurrencias) {
                            const startDate = new Date(r.start_date);
                            // iterate days from startRange to endRange
                            for (let d = new Date(startRange); d <= endRange; d.setDate(d.getDate() + 1)) {
                                const weekday = d.getDay();
                                if (weekday !== Number(r.dia_semana)) continue;
                                // only after r.start_date
                                if (d < startDate) continue;
                                // respect optional end_date on recurrence: skip occurrences after end_date
                                if (r.end_date) {
                                    const endDateObj = new Date(String(r.end_date));
                                    if (d > endDateObj) continue;
                                }
                                const dateIso = d.toISOString().split('T')[0];

                                // Skip if there's an exception for this recurrencia on this date
                                const hasException = excepciones.some(ex => Number(ex.recurrencia_id) === Number(r.id) && String(ex.fecha) === dateIso);
                                if (hasException) continue;

                                const fechaInicioIso = new Date(dateIso + 'T' + r.hora_inicio).toISOString();
                                if (existingStarts.has(fechaInicioIso)) continue; // avoid duplicates

                                const dur = Number(r.duracion_minutos || r.duracionMinutos || 60);
                                const fechaFinIso = new Date(new Date(fechaInicioIso).getTime() + dur * 60000).toISOString();

                                // create synthetic negative id to avoid collision with DB ids
                                const recId = Number(r.id);
                                const dayOffset = Math.round((new Date(fechaInicioIso).getTime() - new Date(r.start_date).getTime()) / (24*60*60000));
                                const syntheticId = -(recId * 10000 + Math.max(0, dayOffset));

                                generated.push({
                                    id: syntheticId as any,
                                    cancha_id: r.cancha_id,
                                    fecha_inicio: fechaInicioIso,
                                    fecha_fin: fechaFinIso,
                                    precio: Number(r.precio || 0),
                                    estado: r.estado || 'reservado',
                                    socio_titular_id: r.socio_titular_id,
                                    notas: r.notas,
                                    creado_por: r.creado_por,
                                    creado_en: r.creado_en,
                                    club_id: r.club_id,
                                    cancha: canchasRes.data ? (canchasRes.data as any[]).find((c:any)=>c.id===r.cancha_id) : undefined,
                                    socio_titular: (sociosArr || []).find((s: any) => String(s.id) === String(r.socio_titular_id)),
                                    participantes: []
                                } as unknown as Turno);
                            }
                        }

                        if (generated.length > 0) {
                            // merge and sort by fecha_inicio desc
                            const merged = [...generated, ...baseTurnos];
                            merged.sort((a,b) => new Date(String(b.fecha_inicio)).getTime() - new Date(String(a.fecha_inicio)).getTime());
                            setTurnos(merged as Turno[]);
                        }
                    }
                } catch (e) {
                    // If the recurrencias or excepciones tables don't exist or queries fail, ignore silently
                    console.warn('No se pudieron cargar recurrencias de turnos o excepciones:', e?.message || e);
                }
        } catch (turnosErr: any) {
            console.warn("No se pudo cargar el módulo de turnos (Posiblemente faltan las tablas en DB):", turnosErr.message);
            // No hacemos setError global para permitir usar el resto de la app
            setCanchas([]);
            setTurnos([]);
        }

    } catch(err: any) {
        console.error("Error al cargar datos del club:", err);
        throw new Error(getErrorMessage(err, "No se pudieron cargar los datos."));
    }
  };
  
  const fetchPlanes = async () => {
    const { data, error } = await supabase.from('planes').select('*').order('precio', { ascending: true });
    if (!error) setPlanes(data || []);
  };

  const fetchSuperAdminData = async () => {
      try {
          const { data: clubesData } = await supabase.rpc('get_clubes_con_conteo_socios');
          setClubes(clubesData || []);
          const { data: logsData } = await supabase.from('auditoria_eventos').select('*').order('creado_en', { ascending: false }).limit(100);
          setAuditLogs(logsData || []);
      } catch (err: any) {
          console.error("Error al cargar datos de SuperAdmin:", err);
      }
  }

  useEffect(() => {
    setLoading(true);

    // Procesar redirect de magic link / OTP si existe en la URL.
    (async () => {
        try {
            const authAny: any = supabase.auth;
            if (typeof authAny.getSessionFromUrl === 'function') {
                // Método recomendado cuando está disponible
                const maybe = await authAny.getSessionFromUrl({ storeSession: true });
                console.log('getSessionFromUrl:', maybe);
            } else {
                // Fallback: intentar parsear tokens desde el fragmento o query de la URL
                console.warn('getSessionFromUrl not available in this supabase client version. Trying URL token fallback.');
                const hash = window.location.hash || '';
                const query = window.location.search || '';
                const parseKV = (str: string) => {
                    if (!str) return {};
                    return str.replace(/^#|^\?/, '').split('&').map(s => s.split('=').map(decodeURIComponent)).reduce((acc: any, [k, v]: any) => {
                        if (k) acc[k] = v; return acc;
                    }, {});
                };
                const hashParams = parseKV(hash);
                const queryParams = parseKV(query);
                const access_token = hashParams['access_token'] || queryParams['access_token'];
                const refresh_token = hashParams['refresh_token'] || queryParams['refresh_token'];
                if (access_token) {
                    try {
                        if (typeof authAny.setSession === 'function') {
                            await authAny.setSession({ access_token, refresh_token });
                        }
                        try { window.history.replaceState({}, document.title, window.location.origin + window.location.pathname); } catch(e){}
                    } catch (e) {
                        // silently ignore
                    }
                } else {
                    // no tokens found; nothing to do
                }
            }

            try {
                const current = await supabase.auth.getSession();
                // session inspected
            } catch (e) {
                // ignore
            }
            try {
                const u = await supabase.auth.getUser();
                // user inspected
            } catch (e) {
                // ignore
            }
        } catch (e) {
            // fallback failed or no session present
        }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        (async () => {
            try {
                if (session?.user) {
                    // Intentamos obtener el profile del usuario. Primero intentamos incluir avatar_url;
                    // si eso falla (columna no existe), reintentamos sin ese campo.
                    const getProfileSafe = async (userId: string) => {
                        try {
                            const { data, error } = await supabase
                                .from('profiles')
                                .select('nombre, rol, club_id, club:clubes(nombre), avatar_url')
                                .eq('id', userId)
                                .maybeSingle();
                            if (error) throw error;
                            return { data, error: null };
                        } catch (err: any) {
                            // Si la causa es que la columna avatar_url no existe, reintentamos sin ella.
                            const msg = String(err?.message || err || '');
                            if (msg.includes('avatar_url')) {
                                try {
                                    const { data, error } = await supabase
                                        .from('profiles')
                                        .select('nombre, rol, club_id, club:clubes(nombre)')
                                        .eq('id', userId)
                                        .maybeSingle();
                                    return { data, error };
                                } catch (err2: any) {
                                    return { data: null, error: err2 };
                                }
                            }
                            return { data: null, error: err };
                        }
                    };

                    let profile: any = null;
                    let profileError: any = null;
                    if (session?.user) {
                        const res = await getProfileSafe(session.user.id);
                        profile = res.data;
                        profileError = res.error;
                    }

                    if (profileError) {
                        console.warn('No se pudo leer profile (se continuará con fallback si aplica):', profileError.message || profileError);
                    }

                    // Si no existe profile, intentamos un fallback consultando la tabla `socios` por email.
                    // Esto cubre el caso donde el socio fue creado solo en la tabla `socios` y no en `profiles`.
                    if (!profile) {
                        try {
                            if (session.user.email) {
                                const { data: socioByEmail, error: socioErr } = await supabase.from('socios').select('*').eq('email', session.user.email).maybeSingle();
                                if (!socioErr && socioByEmail) {
                                    // Construimos un objeto similar al profile esperado
                                    profile = {
                                        nombre: socioByEmail.nombre,
                                        rol: Rol.SOCIO,
                                        club_id: socioByEmail.club_id,
                                        avatar_url: socioByEmail.avatar_url
                                    };
                                }
                            }
                        } catch (socioLookupErr: any) {
                            console.warn('Fallback lookup in socios by email failed:', socioLookupErr.message || socioLookupErr);
                        }
                    }

                          if (!profile && session.user.user_metadata?.is_new_club_signup) {
                        // Lógica de fallback para nuevo registro si falla la primera vez (simplificada)
                         const { nombre, club_nombre } = session.user.user_metadata;
                         const { data: clubData } = await supabase.from('clubes').insert({ nombre: club_nombre, plan: 'Pro', estado: 'activo', suscripcion_estado: 'trialing', administrador_id: session.user.id }).select('id').single();
                         if(clubData) {
                                      // Usamos upsert para evitar conflicto si el profile ya existe por alguna razón.
                                      // Evitamos solicitar `avatar_url` aquí (puede no existir todavía en la tabla).
                                      const { data: newProfile, error: upsertErr } = await supabase.from('profiles')
                                          .upsert({ id: session.user.id, nombre, rol: Rol.ADMIN, club_id: clubData.id }, { onConflict: 'id' })
                                          .select('nombre, rol, club_id, club:clubes(nombre)')
                                          .maybeSingle();
                                      if (upsertErr) console.warn('Upsert profile error:', upsertErr.message || upsertErr);
                                      profile = newProfile;
                         }
                    }
                    
                    if (profile) {
                        const currentUser: Usuario = {
                            id: session.user.id,
                            nombre: profile.nombre,
                            rol: profile.rol as Rol,
                            email: session.user.email,
                            club_id: profile.club_id,
                            club_nombre: profile.club ? (profile.club as unknown as { nombre: string }).nombre : undefined,
                            avatar_url: profile.avatar_url
                        };
                        setUser(currentUser);
                        // Si el usuario es un socio, preseleccionamos la pantalla de perfil/portal
                        try {
                            if (currentUser.rol === Rol.SOCIO) {
                                setActiveScreen('perfil');
                            }
                        } catch(e) {
                            // no crítico: si setActiveScreen no está disponible, ignoramos
                            console.warn('No se pudo setear pantalla por rol socio:', e);
                        }
                        if (currentUser.rol !== Rol.SOCIO) await fetchPlanes();
                        if (currentUser.rol === Rol.SUPERADMIN) await fetchSuperAdminData();
                        else if (currentUser.club_id) await fetchClubData(currentUser.club_id);
                    }
                } else {
                    setUser(null);
                    setClub(null);
                }
            } catch (err: any) {
                console.error("Init Error:", err);
                setError(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        })();
    });
    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
      setLoginError(null);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setLoginError(getErrorMessage(error));
  }

  const logout = async () => {
        try {
            const res: any = await supabase.auth.signOut();
            // supabase-js versions differ: some return an object with `error`, others throw.
            if (res?.error) {
                const err = res.error;
                const msg = String(err?.message || err || '');
                if (!msg.includes('session_not_found')) {
                    setError(getErrorMessage(err));
                } else {
                    console.warn('signOut: session_not_found - ignoring and continuing logout flow.');
                }
            }
        } catch (err: any) {
            const msg = String(err?.message || err || '');
            if (!msg.includes('session_not_found')) {
                setError(getErrorMessage(err));
            } else {
                console.warn('signOut threw session_not_found - ignoring and continuing logout flow.');
            }
        } finally {
            // Clear client-side state regardless of server response
            setUser(null);
            setClub(null);
            // Clear any existing errors so the app doesn't stay on the error screen
            setError(null);
            setLoginError(null);
            setRegistrationError(null);
            setUnauthenticatedView('landing');
        }
  }

    const clearError = () => {
            setError(null);
    }

  const registerAndCreateClub = async (adminName: string, email: string, password: string, clubName: string) => {
    setRegistrationError(null);
    try {
        const { error, data } = await supabase.auth.signUp({
            email,
            password,
            options: { data: { nombre: adminName, club_nombre: clubName, is_new_club_signup: true } }
        });

        if (error) {
            setRegistrationError(getErrorMessage(error));
            return false;
        }

        // Si el signup fue exitoso, devolvemos true. La creación del club en la DB
        // se realiza en el servidor o por políticas específicas. Aquí solo
        // confirmamos que el registro del usuario fue aceptado.
        return true;

    } catch(err: any) {
        setRegistrationError(getErrorMessage(err));
        return false;
    }
  };

    const registerSocio = async (nombre: string, email: string, password: string, clubId?: number) => {
        setRegistrationError(null);
        try {
            const { error, data } = await supabase.auth.signUp({
                email,
                password,
                options: { data: { nombre, is_socio_self_signup: true, club_id: clubId } }
            });

            if (error) {
                setRegistrationError(getErrorMessage(error));
                return false;
            }

            // If supabase returns a user id immediately (rare when auto-confirmation enabled), create socios row linked to user.
            const userId = data?.user?.id || null;
            try {
                if (userId) {
                    await supabase.from('socios').insert([{ user_id: userId, email, nombre, club_id: clubId }]);
                } else {
                    // Create a pending socio record (without user_id) so admin can link later or the onAuth callback will resolve by email lookup
                    await supabase.from('socios').insert([{ email, nombre, club_id: clubId, estado: 'pendiente' }]);
                }
            } catch (insertErr: any) {
                // Non-fatal: user was created but socios row failed. Log and continue.
                console.warn('No se pudo crear la fila en socios tras signup:', insertErr?.message || insertErr);
            }

            return true;
        } catch (err: any) {
            setRegistrationError(getErrorMessage(err));
            return false;
        }
    };
  
  const readOnlyGuard = () => {
    if (isReadOnly) {
        try {
            ui.showToast('info', 'Suscripción vencida. Solo lectura.');
        } catch (e) {
            // Fallback si el contexto no está disponible
            console.warn("Suscripción vencida. Solo lectura.");
        }
        return true;
    }
    return false;
  }

  const addSocio = async (newSocioData: any): Promise<Socio | null> => {
    if (readOnlyGuard()) return null;
    try {
        // Normalize documento: trim and convert empty string to null to avoid unique index collisions on ''
        const documentoRaw = typeof newSocioData.documento === 'string' ? newSocioData.documento.trim() : newSocioData.documento;
        const documento = documentoRaw ? documentoRaw : null;

        // Validar que email, telefono y documento sean obligatorios
        if (!documento || documento.trim() === '') {
            try { ui.showModal('Error', 'El DNI es obligatorio.'); } catch(e) { console.warn('El DNI es obligatorio.'); }
            return null;
        }
        
        const email = typeof newSocioData.email === 'string' ? newSocioData.email.trim() : newSocioData.email;
        if (!email || email === '') {
            try { ui.showModal('Error', 'El email es obligatorio.'); } catch(e) { console.warn('El email es obligatorio.'); }
            return null;
        }

        const telefono = typeof newSocioData.telefono === 'string' ? newSocioData.telefono.trim() : newSocioData.telefono;
        if (!telefono || telefono === '') {
            try { ui.showModal('Error', 'El teléfono es obligatorio.'); } catch(e) { console.warn('El teléfono es obligatorio.'); }
            return null;
        }

        const payload = { ...newSocioData, documento, email, telefono, creado_por: user?.nombre, club_id: user?.club_id };

        // If documento provided, check for an existing socio with same documento to provide a friendlier message
        if (documento) {
            try {
                const { data: existing, error: existsErr } = await supabase.from('socios').select('id').eq('club_id', user?.club_id).eq('documento', documento).maybeSingle();
                if (existsErr) console.warn('Error comprobando documento existente:', existsErr);
                if (existing) {
                    try { ui.showModal('Error', 'Ya existe un socio con ese documento.'); } catch(e) { console.warn('Ya existe un socio con ese documento.'); }
                    return null;
                }
            } catch(e) {
                console.warn('Comprobación de documento falló:', e);
            }
        }

        const { data, error } = await supabase.from('socios').insert([payload]).select();
        if (error) {
            const friendly = getErrorMessage(error);
            // Handle unique constraint errors gracefully without setear el error global que bloquea la app
            if (String(friendly).toLowerCase().includes('duplicate key') || String(friendly).toLowerCase().includes('unique')) {
                try { ui.showModal('Error', 'Ya existe un socio con ese documento.'); } catch(e) { console.warn(friendly); }
                return null;
            }
            setError(friendly);
            return null;
        }
        if (data) {
            setSocios(prev => [...prev, data[0]]);
            return data[0];
        }
        return null;
    } catch (err: any) {
        const msg = getErrorMessage(err);
        try { ui.showModal('Error', msg); } catch(e) { console.warn(msg); }
        return null;
    }
  };

  const bulkAddSocios = async (
    rows: Array<Pick<Socio, 'nombre' | 'apellido' | 'telefono' | 'email' | 'notas'>>
  ): Promise<{ inserted: number; failed: number; error?: string }> => {
    if (readOnlyGuard()) return { inserted: 0, failed: rows.length, error: 'Solo lectura' };
    if (!user?.club_id) return { inserted: 0, failed: rows.length, error: 'No hay club activo' };
    if (rows.length === 0) return { inserted: 0, failed: 0 };

    const payloads = rows.map(r => ({
        nombre: r.nombre,
        apellido: r.apellido || '',
        telefono: r.telefono || '',
        email: r.email && r.email.trim() ? r.email.trim() : null,
        notas: r.notas || '',
        documento: null,
        estado: 'activo' as const,
        creado_por: user?.nombre,
        club_id: user?.club_id,
    }));

    const CHUNK = 50;
    let inserted = 0;
    let failed = 0;
    let firstError: string | undefined;
    const insertedRows: Socio[] = [];

    for (let i = 0; i < payloads.length; i += CHUNK) {
        const chunk = payloads.slice(i, i + CHUNK);
        const { data, error } = await supabase.from('socios').insert(chunk).select();
        if (error) {
            failed += chunk.length;
            if (!firstError) firstError = getErrorMessage(error);
            continue;
        }
        if (data) {
            inserted += data.length;
            insertedRows.push(...(data as Socio[]));
        }
    }

    if (insertedRows.length > 0) {
        setSocios(prev => [...prev, ...insertedRows]);
    }
    return { inserted, failed, error: firstError };
  };

  const updateSocio = async (updatedSocio: Socio): Promise<boolean> => {
    if (readOnlyGuard()) return false;
    
    // Validar que email, telefono y documento sean obligatorios
    const documento = typeof updatedSocio.documento === 'string' ? updatedSocio.documento.trim() : updatedSocio.documento;
    if (!documento || documento === '') {
        try { ui.showModal('Error', 'El DNI es obligatorio.'); } catch(e) { console.warn('El DNI es obligatorio.'); }
        return false;
    }
    
    const email = typeof updatedSocio.email === 'string' ? updatedSocio.email.trim() : updatedSocio.email;
    if (!email || email === '') {
        try { ui.showModal('Error', 'El email es obligatorio.'); } catch(e) { console.warn('El email es obligatorio.'); }
        return false;
    }

    const telefono = typeof updatedSocio.telefono === 'string' ? updatedSocio.telefono.trim() : updatedSocio.telefono;
    if (!telefono || telefono === '') {
        try { ui.showModal('Error', 'El teléfono es obligatorio.'); } catch(e) { console.warn('El teléfono es obligatorio.'); }
        return false;
    }
    
    const dataToUpdate = { ...updatedSocio, documento, email, telefono };
    const { data, error } = await supabase.from('socios').update(dataToUpdate).eq('id', updatedSocio.id).select();
    if (error) { setError(getErrorMessage(error)); return false; }
    if (data) { setSocios(prev => prev.map(s => s.id === updatedSocio.id ? data[0] : s)); return true; }
    return false;
  };

  const deleteSocio = async (id: number): Promise<{ success: boolean; message?: string }> => {
    if (!user) return { success: false, message: "No autenticado." };
    if (readOnlyGuard()) return { success: false, message: "Modo solo lectura activo." };
    if (user.rol !== Rol.ADMIN) return { success: false, message: "Solo el administrador puede inactivar socios." };

    try {
        // Antes de eliminar, comprobamos si existen ventas que referencian al socio.
        try {
            const { error: ventasErr, count: ventasCount } = await supabase.from('ventas').select('id', { count: 'exact' }).eq('socio_id', id);
            if (ventasErr) console.warn('Error comprobando ventas relacionadas:', ventasErr);
                if (typeof ventasCount === 'number' && ventasCount > 0) {
                // No permitimos inactivar si hay ventas históricas que lo referencian.
                try { ui.showModal('Error', 'No se puede inactivar el socio porque tiene ventas registradas.'); } catch(e) { console.warn('No se puede inactivar el socio: tiene ventas registradas.'); }
                return { success: false, message: 'El socio tiene ventas asociadas y no puede ser inactivado.' };
            }
        } catch(e) {
            console.warn('Fallo comprobación ventas antes de eliminar socio:', e);
            // Continuamos y dejamos que la eliminación se intente, si la comprobación falla no bloqueamos la operación
        }

        // Soft-delete: marcar el socio como 'inactivo' en lugar de borrarlo.
        // Esto preserva el historial (ventas) y evita errores FK.
        const { data: updated, error } = await supabase
            .from('socios')
            .update({ estado: 'inactivo' })
            .eq('id', id)
            .select();

        if (error) {
            const msg = getErrorMessage(error);
            try { ui.showModal('Error', msg); } catch(e) { console.warn(msg); }
            return { success: false, message: msg };
        }

        if (!updated || (Array.isArray(updated) && updated.length === 0)) {
            return { success: false, message: 'No se pudo inactivar el socio. Puede que no exista o no tengas permisos.' };
        }

        // Actualizamos el estado local con el registro actualizado
        const updatedRecord = Array.isArray(updated) ? updated[0] : updated;
        setSocios(prev => prev.map(s => s.id === id ? updatedRecord : s));
        return { success: true };

    } catch (err: any) {
        return { success: false, message: err.message };
    }
  };
  
  // ---- GESTIÓN DE STAFF (usuarios con rol Cajero / Viewer) ----
  // Las operaciones de auth.users requieren service_role_key, por eso se canalizan
  // a la edge function "manage-staff-user" que valida que el caller sea Admin del club.

  const createStaffUser = async (data: { nombre: string; email: string; rol: 'Cajero' | 'Viewer' }): Promise<{ success: boolean; user?: Usuario; message?: string }> => {
    if (!user) return { success: false, message: 'No autenticado.' };
    if (user.rol !== Rol.ADMIN) return { success: false, message: 'Solo el Admin puede crear usuarios.' };
    if (readOnlyGuard()) return { success: false, message: 'Modo solo lectura activo.' };
    try {
      const { data: resp, error } = await supabase.functions.invoke('manage-staff-user', {
        body: { action: 'create', nombre: data.nombre, email: data.email, rol: data.rol },
      });
      if (error) return { success: false, message: error.message || 'Error invocando la función.' };
      if (!resp?.ok) return { success: false, message: resp?.error || 'Error desconocido.' };

      const created = resp.user as Usuario;
      setUsuarios(prev => [...prev, created]);

      // Disparar el envío del magic link de acceso para que el nuevo usuario pueda entrar.
      try {
        const redirectTo = window.location.origin + '/';
        await supabase.auth.signInWithOtp({ email: resp.email || data.email, options: { emailRedirectTo: redirectTo } });
      } catch (e) {
        console.warn('No se pudo enviar el magic link automáticamente:', e);
      }
      return { success: true, user: created };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Error inesperado.' };
    }
  };

  const updateStaffUser = async (id: string, patch: { nombre?: string; rol?: 'Cajero' | 'Viewer' }): Promise<{ success: boolean; user?: Usuario; message?: string }> => {
    if (!user) return { success: false, message: 'No autenticado.' };
    if (user.rol !== Rol.ADMIN) return { success: false, message: 'Solo el Admin puede editar usuarios.' };
    if (readOnlyGuard()) return { success: false, message: 'Modo solo lectura activo.' };
    try {
      const { data: resp, error } = await supabase.functions.invoke('manage-staff-user', {
        body: { action: 'update', id, ...patch },
      });
      if (error) return { success: false, message: error.message || 'Error invocando la función.' };
      if (!resp?.ok) return { success: false, message: resp?.error || 'Error desconocido.' };
      const updated = resp.user as Usuario;
      setUsuarios(prev => prev.map(u => u.id === id ? { ...u, ...updated } : u));
      return { success: true, user: updated };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Error inesperado.' };
    }
  };

  const deleteStaffUser = async (id: string): Promise<{ success: boolean; message?: string }> => {
    if (!user) return { success: false, message: 'No autenticado.' };
    if (user.rol !== Rol.ADMIN) return { success: false, message: 'Solo el Admin puede eliminar usuarios.' };
    if (readOnlyGuard()) return { success: false, message: 'Modo solo lectura activo.' };
    if (id === user.id) return { success: false, message: 'No podés eliminarte a vos mismo.' };
    try {
      const { data: resp, error } = await supabase.functions.invoke('manage-staff-user', {
        body: { action: 'delete', id },
      });
      if (error) return { success: false, message: error.message || 'Error invocando la función.' };
      if (!resp?.ok) return { success: false, message: resp?.error || 'Error desconocido.' };
      setUsuarios(prev => prev.filter(u => u.id !== id));
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Error inesperado.' };
    }
  };

  const hardDeleteSocio = async (id: number): Promise<{ success: boolean; message?: string }> => {
    if (!user) return { success: false, message: "No autenticado." };
    if (readOnlyGuard()) return { success: false, message: "Modo solo lectura activo." };
    if (user.rol !== Rol.ADMIN) return { success: false, message: "Solo el administrador puede eliminar socios." };

    try {
        // 0) Bloquear si tiene deudas pendientes (regla de negocio: no perder rastro).
        try {
            const { data: deudasPend } = await supabase.from('deudas').select('id').eq('socio_id', id).eq('estado', 'pendiente').limit(1);
            if (deudasPend && deudasPend.length > 0) {
                return { success: false, message: 'No se puede eliminar: el socio tiene deudas pendientes. Cobralas o cancelalas antes.' };
            }
        } catch (e) {
            console.warn('No se pudo verificar deudas antes de eliminar socio:', e);
        }

        // 1) Borrar movimientos de puntos del socio (no tienen ON DELETE CASCADE)
        const { error: movErr } = await supabase.from('movimientos_puntos').delete().eq('socio_id', id);
        if (movErr) {
            const msg = getErrorMessage(movErr);
            return { success: false, message: `Error eliminando movimientos de puntos: ${msg}` };
        }

        // 2) Borrar ventas del socio (cascada borra venta_items)
        const { error: ventasErr } = await supabase.from('ventas').delete().eq('socio_id', id);
        if (ventasErr) {
            const msg = getErrorMessage(ventasErr);
            return { success: false, message: `Error eliminando ventas asociadas: ${msg}` };
        }

        // 3) Borrar el socio (cascada borra turno_participantes, set null en turnos.socio_titular_id)
        const { data: deleted, error } = await supabase.from('socios').delete().eq('id', id).select();
        if (error) {
            const msg = getErrorMessage(error);
            return { success: false, message: msg };
        }
        if (!deleted || (Array.isArray(deleted) && deleted.length === 0)) {
            return { success: false, message: 'No se pudo eliminar el socio. Puede que no exista o no tengas permisos.' };
        }

        // Actualizar estado local
        setSocios(prev => prev.filter(s => s.id !== id));
        setMovimientosPuntos(prev => prev.filter(m => m.socio_id !== id));
        return { success: true };

    } catch (err: any) {
        return { success: false, message: err.message };
    }
  };

  // --- DEUDAS ---

  // Crear una o varias deudas asociadas a un socio (típicamente al cobrar un turno).
  const addDeudas = async (inputs: DeudaInput[]): Promise<{ success: boolean; created?: Deuda[]; message?: string }> => {
      if (!user) return { success: false, message: "No autenticado." };
      if (readOnlyGuard()) return { success: false, message: "Modo solo lectura activo." };
      if (user.rol !== Rol.ADMIN && user.rol !== Rol.CAJERO) {
          return { success: false, message: "Solo Admin o Cajero pueden registrar deudas." };
      }
      const rows = inputs
          .filter(d => d.socio_id && d.monto > 0)
          .map(d => ({
              socio_id: String(d.socio_id),
              monto: d.monto,
              descripcion: d.descripcion ?? null,
              turno_id: d.turno_id ?? null,
              estado: 'pendiente' as const,
              creado_por: user.nombre,
              club_id: user.club_id,
          }));
      if (rows.length === 0) return { success: true, created: [] };
      const { data, error } = await supabase.from('deudas').insert(rows).select('*, socio:socios(*)');
      if (error) {
          const msg = getErrorMessage(error);
          return { success: false, message: msg };
      }
      const created = (data || []) as Deuda[];
      setDeudas(prev => [...created, ...prev]);
      return { success: true, created };
  };

  // Cobrar una o varias deudas (de un mismo socio) registrando una venta en caja.
  // No genera puntos (los puntos ya se acreditaron al marcar el turno como pagado en su momento).
  const cobrarDeudas = async (ids: number[], medioDePago: MedioDePago): Promise<{ success: boolean; message?: string }> => {
      if (!user) return { success: false, message: "No autenticado." };
      if (readOnlyGuard()) return { success: false, message: "Modo solo lectura activo." };
      if (user.rol !== Rol.ADMIN && user.rol !== Rol.CAJERO) {
          return { success: false, message: "Solo Admin o Cajero pueden cobrar deudas." };
      }
      if (!ids || ids.length === 0) return { success: false, message: "No se indicaron deudas a cobrar." };
      if (!sesionCaja || sesionCaja.estado !== 'abierta') {
          return { success: false, message: "Para cobrar deudas necesitás tener la CAJA ABIERTA." };
      }

      const toCobrar = deudas.filter(d => ids.includes(d.id) && d.estado === 'pendiente');
      if (toCobrar.length === 0) return { success: false, message: "Las deudas indicadas ya no están pendientes." };

      // Validar que todas sean del mismo socio (en este flujo cobramos a un socio por vez).
      const socioIds = Array.from(new Set(toCobrar.map(d => String(d.socio_id))));
      if (socioIds.length > 1) return { success: false, message: "Las deudas a cobrar deben ser todas del mismo socio." };
      const socioId = socioIds[0];
      const total = toCobrar.reduce((s, d) => s + Number(d.monto), 0);

      // Buscar/crear producto genérico "Cobro de deuda" para registrar la venta.
      let productoDeuda = productos.find(p => p.nombre === 'Cobro de deuda');
      if (!productoDeuda) {
          const created = await addProducto({
              nombre: 'Cobro de deuda',
              categoria: 'Servicios',
              precio: 0,
              estado: 'activo',
              canjeable_por_puntos: false
          });
          productoDeuda = created || undefined;
      }
      if (!productoDeuda) return { success: false, message: "No se pudo crear el producto interno 'Cobro de deuda'." };

      const item = {
          producto: productoDeuda,
          producto_id: productoDeuda.id,
          cantidad: 1,
          precio_unitario: total,
      };

      const ok = await registrarVenta({
          socio_id: socioId as any,
          items: [item],
          total,
          medio_de_pago: medioDePago,
          puntos_ganados: 0, // sin puntos: ya se acreditaron al cobrar el turno
          puntos_canjeados: 0,
          socios_puntos_ids: []
      });
      if (!ok) return { success: false, message: "No se pudo registrar la venta en caja." };

      // Marcar deudas como pagadas. Asociamos a la última venta del socio (la que acabamos de crear).
      // Tomamos la venta más reciente del estado local.
      const ventasLocales = (sesionCaja?.ventas || []) as any[];
      const ultimaVenta = ventasLocales.length > 0 ? ventasLocales[0] : null;
      const { error } = await supabase.from('deudas').update({
          estado: 'pagada',
          venta_id: ultimaVenta?.id ?? null,
          pagada_por: user.nombre,
          pagada_en: new Date().toISOString()
      }).in('id', ids);

      if (error) {
          // La venta ya quedó registrada; avisamos para revisión manual.
          return { success: false, message: `Venta registrada, pero no se pudieron marcar las deudas como pagadas: ${getErrorMessage(error)}` };
      }
      setDeudas(prev => prev.map(d => ids.includes(d.id) ? { ...d, estado: 'pagada', venta_id: ultimaVenta?.id ?? null, pagada_por: user.nombre, pagada_en: new Date().toISOString() } : d));
      return { success: true };
  };

  const cancelarDeuda = async (id: number): Promise<{ success: boolean; message?: string }> => {
      if (!user) return { success: false, message: "No autenticado." };
      if (readOnlyGuard()) return { success: false, message: "Modo solo lectura activo." };
      if (user.rol !== Rol.ADMIN) return { success: false, message: "Solo el administrador puede cancelar deudas." };
      const { error } = await supabase.from('deudas').update({
          estado: 'cancelada',
          cancelada_por: user.nombre,
          cancelada_en: new Date().toISOString()
      }).eq('id', id);
      if (error) return { success: false, message: getErrorMessage(error) };
      setDeudas(prev => prev.map(d => d.id === id ? { ...d, estado: 'cancelada', cancelada_por: user.nombre, cancelada_en: new Date().toISOString() } : d));
      return { success: true };
  };

  const addProducto = async (newProductoData: any): Promise<Producto | null> => {
    if (readOnlyGuard()) return null;
    const { data, error } = await supabase.from('productos').insert([{...newProductoData, club_id: user?.club_id}]).select();
    if (error) { setError(getErrorMessage(error)); return null; }
    if (data) { 
        setProductos(prev => [...prev, data[0]].sort((a,b) => a.nombre.localeCompare(b.nombre))); 
        return data[0]; 
    }
    return null;
  };

  const updateProducto = async (updatedProducto: Producto): Promise<boolean> => {
    if (readOnlyGuard()) return false;
    const { data, error } = await supabase.from('productos').update(updatedProducto).eq('id', updatedProducto.id).select();
    if (error) { setError(getErrorMessage(error)); return false; }
    if (data) { setProductos(prev => prev.map(p => p.id === updatedProducto.id ? data[0] : p)); return true; }
    return false;
  };

  const deleteProducto = async (id: number): Promise<{ success: boolean; message?: string }> => {
    if (!user) return { success: false, message: "No autenticado." };
    if (readOnlyGuard()) return { success: false, message: "Modo solo lectura activo." };
    
    try {
        // Hacemos soft-delete: marcamos el producto como 'inactivo' en lugar de borrarlo.
        // Esto evita violaciones de FK (venta_items -> productos) y mantiene ventas históricas.
        const { data, error } = await supabase
            .from('productos')
            .update({ estado: 'inactivo' })
            .eq('id', id)
            .select();

        if (error) {
            return { success: false, message: getErrorMessage(error) };
        }

        if (!data || data.length === 0) {
            return { success: false, message: "No se encontró el producto o no tenés permisos para modificarlo." };
        }

        // Actualizamos el estado local del producto para reflejar el cambio
        setProductos(prev => prev.map(p => p.id === id ? data[0] : p));
        return { success: true };

    } catch (err: any) {
        return { success: false, message: err.message };
    }
  };
  
  const openCaja = async (monto: number) => {
    if (readOnlyGuard()) return;
    const { data, error } = await supabase.from('sesiones_caja').insert([{abierta_por: user?.nombre, monto_inicial: monto, estado: 'abierta', club_id: user?.club_id}]).select('*, ventas(*, socio:socios(*), items:venta_items(*, producto:productos(*)))').single();
    if (!error && data) setSesionCaja(data as any);
  };

  const closeCaja = async (monto: number) => {
    if (readOnlyGuard() || !sesionCaja) return;
    const { data, error } = await supabase.from('sesiones_caja').update({cerrada_por: user?.nombre, monto_final: monto, cerrada_en: new Date().toISOString(), estado: 'cerrada'}).eq('id', sesionCaja.id).select('*, ventas(*, socio:socios(*), items:venta_items(*, producto:productos(*)))').single();
    if (!error && data) setSesionCaja(data as any);
  };

  const registrarVenta = async (ventaData: any): Promise<boolean> => {
    if (readOnlyGuard() || !sesionCaja) return false;
    try {
        // CORRECCIÓN: Extraemos explicitamente el socio_id del objeto socio si existe
        const socioId = ventaData.socio ? ventaData.socio.id : (ventaData.socio_id || null);

        // Calcular puntos antes de insertar la venta
        let puntosGanados = Number(ventaData.puntos_ganados || 0);
        const puntosCanjeados = Number(ventaData.puntos_canjeados || 0);

        if (puntosGanados === 0 && Array.isArray(ventaData.items) && ventaData.items.length > 0) {
            try {
                let computed = 0;
                for (const item of ventaData.items) {
                    const categoriaItem = (item?.producto?.categoria || '').toLowerCase();
                    const reglaCategoria = reglasPuntos.find(r => (r.categoria || '').toLowerCase() === categoriaItem);
                    const reglaGeneral = reglasPuntos.find(r => !r.categoria);
                    const regla = reglaCategoria || reglaGeneral;
                    if (regla && regla.cada > 0) {
                        const subtotal = Number(item.precio_unitario || 0) * Number(item.cantidad || 0);
                        computed += Math.floor(subtotal / regla.cada) * regla.puntos;
                    }
                }
                if (computed > 0) puntosGanados = computed;
            } catch (e) {
                console.warn('No se pudieron calcular puntos automáticamente:', e);
            }
        }

        // FIX: Construimos el payload explícitamente para evitar que campos como 'items' 
        // se filtren al insert de la tabla 'ventas', lo cual causa errores en Supabase.
        // NOTA: Se eliminó 'sesion_caja_id' temporalmente porque algunos esquemas de BD reportan error PGRST204.
        const insertPayload: any = {
            // La columna en la tabla es `sesion_id` (no `sesion_caja_id`), por eso la guardamos así.
            sesion_id: sesionCaja.id,
            creado_por: user?.nombre,
            club_id: user?.club_id,
            socio_id: socioId,
            total: ventaData.total,
            medio_de_pago: ventaData.medio_de_pago,
            puntos_ganados: puntosGanados,
            puntos_canjeados: puntosCanjeados
        };
        if (ventaData.turno_id) insertPayload.turno_id = ventaData.turno_id;

        const { data: venta, error } = await supabase.from('ventas').insert([insertPayload]).select().single();
        if (error) throw error;
        
        // Insert items separately
        const items = ventaData.items.map((i: any) => ({
            venta_id: venta.id, 
            producto_id: i.producto_id, 
            cantidad: i.cantidad, 
            precio_unitario: i.precio_unitario, 
            club_id: user?.club_id
        }));
        
        const { error: itemsError } = await supabase.from('venta_items').insert(items);
        if (itemsError) throw itemsError;
        
        // Update points if necessary
        const sociosParaPuntos = Array.from(
            new Set(
                ([] as (string | number | null | undefined)[])
                    .concat(ventaData.socios_puntos_ids || [])
                    .concat(socioId ? [socioId] : [])
                    .filter(Boolean)
                    .map(id => String(id))
            )
        );

        if (sociosParaPuntos.length > 0 && (puntosGanados !== 0 || puntosCanjeados !== 0)) {
            const nuevosPuntajes: Record<string, number> = {};
            await Promise.all(
                sociosParaPuntos.map(async (sid) => {
                    let currentPuntos = 0;
                    if (ventaData.socio && String(ventaData.socio.id) === String(sid)) {
                        currentPuntos = ventaData.socio.puntos;
                    } else {
                        const s = socios.find(so => String(so.id) === String(sid));
                        if (s) currentPuntos = s.puntos;
                    }

                    const delta = String(sid) === String(socioId) ? (puntosGanados - puntosCanjeados) : puntosGanados;
                    const nuevoPuntaje = (currentPuntos || 0) + delta;
                    nuevosPuntajes[String(sid)] = nuevoPuntaje;
                    await supabase.from('socios').update({ puntos: nuevoPuntaje }).eq('id', String(sid));
                })
            );

            setSocios(prev => prev.map(s => {
                const key = String(s.id);
                return nuevosPuntajes[key] !== undefined ? { ...s, puntos: nuevosPuntajes[key] } : s;
            }));

            // Crear un movimiento de puntos para que el historial lo muestre (ganados y/o canjeados)
            try {
                const movimientosToInsert: any[] = [];
                sociosParaPuntos.forEach((sid) => {
                    if (puntosGanados > 0) {
                        movimientosToInsert.push({
                            socio_id: String(sid),
                            puntos: puntosGanados,
                            descripcion: `Puntos ganados por venta (venta_id: ${venta.id || 'pendiente'})`,
                            creado_por: user?.nombre,
                            club_id: user?.club_id
                        });
                    }
                    if (String(sid) === String(socioId) && puntosCanjeados > 0) {
                        movimientosToInsert.push({
                            socio_id: String(sid),
                            puntos: -Math.abs(puntosCanjeados),
                            descripcion: `Puntos canjeados en venta (venta_id: ${venta.id || 'pendiente'})`,
                            creado_por: user?.nombre,
                            club_id: user?.club_id
                        });
                    }
                });

                if (movimientosToInsert.length > 0) {
                    const { data: movData, error: movErr } = await supabase.from('movimientos_puntos').insert(movimientosToInsert).select();
                    if (!movErr && movData) {
                        // Añadimos los movimientos al estado local para que el UI los muestre inmediatamente
                        setMovimientosPuntos(prev => [...(movData as any[]).reverse(), ...prev]);
                    }
                }
            } catch (movErr) {
                console.warn('No se pudo crear movimiento de puntos:', movErr);
            }
        }

        // Update local state
        setSesionCaja(prev => prev ? {
            ...prev, 
            ventas: [
                {
                    ...venta, 
                    items: ventaData.items, 
                    socio: ventaData.socio || socios.find(s => s.id === socioId)
                }, 
                ...prev.ventas
            ]
        } : null);
        
        // Show success confirmation toast (5 seconds)
        ui.showToast('success', `✓ Venta registrada exitosamente. Total: $${ventaData.total.toLocaleString('es-AR')}`, 5000);
        
        return true;
        } catch (err: any) {
        console.error("Error en registrarVenta:", err);
        try { ui.showModal('Error', getErrorMessage(err)); } catch(e) { console.warn(getErrorMessage(err)); }
        return false;
    }
  };

  const addReglaPunto = async (reglaData: any): Promise<boolean> => {
    if (readOnlyGuard()) return false;
    const { data, error } = await supabase.from('reglas_puntos').insert([{...reglaData, club_id: user?.club_id}]).select();
    if (error) { setError(getErrorMessage(error)); return false; }
    if (data) { setReglasPuntos(prev => [...prev, data[0]]); return true; }
    return false;
  };

  const updateReglaPunto = async (reglaData: ReglaPuntos): Promise<boolean> => {
    if (readOnlyGuard()) return false;
    const { data, error } = await supabase.from('reglas_puntos').update(reglaData).eq('id', reglaData.id).select();
    if (error) { setError(getErrorMessage(error)); return false; }
    if (data) { setReglasPuntos(prev => prev.map(r => r.id === reglaData.id ? data[0] : r)); return true; }
    return false;
  };

  const deleteReglaPunto = async (id: number): Promise<{ success: boolean; message?: string }> => {
      if(readOnlyGuard()) return { success: false, message: "Modo solo lectura." };
      
      try {
          const { error, count } = await supabase
            .from('reglas_puntos')
            .delete({ count: 'exact' })
            .eq('id', id);
            
          if (error) {
              return { success: false, message: getErrorMessage(error) };
          }
          
          if (count === 0) {
              return { success: false, message: "No se pudo eliminar la regla. Verificá permisos o si ya fue eliminada." };
          }

          setReglasPuntos(prev => prev.filter(r => r.id !== id));
          return { success: true };
      } catch (err: any) {
          return { success: false, message: err.message };
      }
  }

  const updateClubPlan = async (clubId: number, plan: Plan) => {
      const { error } = await supabase.from('clubes').update({ plan }).eq('id', clubId);
      if (!error) setClubes(prev => prev.map(c => c.id === clubId ? { ...c, plan } : c));
  }

  const addPlan = async (planData: any) => {
      const { data, error } = await supabase.from('planes').insert([planData]).select().single();
      if (!error && data) { setPlanes(prev => [...prev, data]); return true; }
      return false;
  }
  
  const updatePlan = async (planData: any) => {
      const { error } = await supabase.from('planes').update(planData).eq('id', planData.id);
      if (!error) { setPlanes(prev => prev.map(p => p.id === planData.id ? planData : p)); return true; }
      return false;
  }

  // --- LOGICA DE TURNOS ---
  const addCancha = async (canchaData: Omit<Cancha, 'id' | 'club_id'>) => {
      if(readOnlyGuard()) return false;
      const { data, error } = await supabase.from('canchas').insert([{...canchaData, club_id: user?.club_id}]).select().single();
      if(error) { setError(getErrorMessage(error)); return false; }
      if(data) { setCanchas(prev => [...prev, data]); return true; }
      return false;
  };

  const updateCancha = async (cancha: Cancha) => {
      if(readOnlyGuard()) return false;
      try {
          const { data, error } = await supabase.from('canchas').update(cancha).eq('id', cancha.id).select();
          if (error) { setError(getErrorMessage(error)); return false; }
          if (data && data.length > 0) {
              const updated = data[0];
              setCanchas(prev => prev.map(c => c.id === updated.id ? updated : c));
              return true;
          }
          return false;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return false;
      }
  };

    const openVentaInCaja = (ventaId: number) => {
            setPendingOpenVentaId(ventaId);
            setActiveScreen('caja');
    };

    const clearPendingOpenVentaId = () => setPendingOpenVentaId(null);

    const openTurnoInTurnos = (turnoId: number) => {
        setPendingOpenTurnoId(turnoId);
        setActiveScreen('turnos');
    };
    const clearPendingOpenTurnoId = () => setPendingOpenTurnoId(null);

  // Convierte el input de participantes (mix de socios y nombres libres) en filas para turno_participantes.
  const buildParticipantesRows = (turnoId: number, participantes: ParticipanteInput[]) => {
      return participantes
          .map(p => {
              const sid = p.socio_id;
              if (sid !== undefined && sid !== null && sid !== 0 && String(sid) !== '0' && String(sid).trim() !== '') {
                  return { turno_id: turnoId, socio_id: String(sid), nombre_libre: null as string | null };
              }
              const nombre = (p.nombre_libre || '').trim();
              if (nombre) {
                  return { turno_id: turnoId, socio_id: null as string | null, nombre_libre: nombre };
              }
              return null;
          })
          .filter((x): x is { turno_id: number; socio_id: string | null; nombre_libre: string | null } => x !== null);
  };

  const addTurno = async (turnoData: Omit<Turno, 'id' | 'creado_por' | 'creado_en' | 'club_id'>, participantes: ParticipanteInput[]): Promise<Turno | null> => {
      if(readOnlyGuard()) return null;
      try {
          console.log('addTurno: payload ->', { turnoData, participantes });
          // 1. Crear el turno
          const { data: turno, error: turnoError } = await supabase
            .from('turnos')
            .insert([{...turnoData, creado_por: user?.nombre, club_id: user?.club_id}])
            .select('*, cancha:canchas(*), socio_titular:socios(*)')
            .single();

          if(turnoError) throw turnoError;
          if(!turno) throw new Error("No se pudo crear el turno");

          // 2. Asociar participantes (socios + nombres libres)
          const participantesData = buildParticipantesRows(turno.id, participantes);

          if(participantesData.length > 0) {
              const { data: partData, error: partError } = await supabase.from('turno_participantes').insert(participantesData).select();
              if(partError) throw partError;
              console.log('addTurno: participantes insertados ->', partData);
          }

          // Refrescar lista de turnos
          const { data: turnoCompleto } = await supabase
            .from('turnos')
            .select('*, cancha:canchas(*), socio_titular:socios(*), participantes:turno_participantes(*, socio:socios(*))')
            .eq('id', turno.id)
            .single();
          console.log('addTurno: turnoCompleto ->', turnoCompleto);

          if(turnoCompleto) {
              const participantesFallback = participantesData.map(row => ({
                  turno_id: turno.id,
                  socio_id: row.socio_id,
                  nombre_libre: row.nombre_libre,
                  socio: row.socio_id ? socios.find(s => String(s.id) === String(row.socio_id)) : undefined
              }));

              const turnoFinal = {
                  ...(turnoCompleto as unknown as Turno),
                  participantes: (turnoCompleto as any).participantes && (turnoCompleto as any).participantes.length > 0
                      ? (turnoCompleto as any).participantes
                      : participantesFallback
              } as Turno;

              setTurnos(prev => [turnoFinal, ...prev]);
              return turnoFinal;
          }
          return turno as Turno;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return null;
      }
  };

  // --- RECURRENCIAS DE TURNOS ---
  const addTurnoRecurrencia = async (recData: any, participantes: ParticipanteInput[]) => {
      if (readOnlyGuard()) return false;
      try {
          // validar campo obligatorio socio_titular_id
          if (!recData.socio_titular_id) {
              setError('Falta socio_titular_id en la recurrencia');
              return false;
          }
          // Ensure notes mark this as a fixed recurrence so UI can detect it
          recData.notas = `[FIJO] ` + (recData.notas || '');

          // Insert recurrencia
          const { data, error } = await supabase.from('turnos_recurrentes').insert([{ ...recData, creado_por: user?.nombre, club_id: user?.club_id }]).select().single();
          if (error) { setError(getErrorMessage(error)); return false; }

          // Create first actual turno on start_date (if desired)
          const startDate = recData.start_date;
          if (startDate) {
              const fechaInicio = new Date(startDate + 'T' + recData.hora_inicio + ':00');
              const fechaFin = new Date(fechaInicio.getTime() + Number(recData.duracion_minutos) * 60000);
              const ok = await addTurno({
                  cancha_id: recData.cancha_id,
                  fecha_inicio: fechaInicio.toISOString(),
                  fecha_fin: fechaFin.toISOString(),
                  precio: recData.precio || 0,
                  estado: recData.estado || 'reservado',
                  socio_titular_id: recData.socio_titular_id,
                  notas: recData.notas
              }, participantes);
              if (!ok) {
                  // if creating the initial turno failed, still consider the recurrencia created but warn
                  console.warn('Recurrencia creada pero no se pudo crear el primer turno');
              }
          }
          return true;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return false;
      }
  };

  const updateTurnoRecurrenciaRecord = async (recId: number, recData: any) => {
      if (readOnlyGuard()) return false;
      try {
          const { error } = await supabase.from('turnos_recurrentes').update(recData).eq('id', recId);
          if (error) throw error;
          // refresh local recurrencias handled on next fetch; optionally update local generated occurrences
          return true;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return false;
      }
  };

  const deleteTurnoRecurrencia = async (recId: number) => {
      if (readOnlyGuard()) return false;
      try {
          const { error } = await supabase.from('turnos_recurrentes').delete().eq('id', recId);
          if (error) throw error;
          // remove generated occurrences from local state (synthetic negative ids)
          setTurnos(prev => prev.filter(t => {
              if (typeof t.id === 'number' && t.id < 0) {
                  const abs = -t.id;
                  const recPart = Math.floor(abs / 10000);
                  return recPart !== recId; // keep if not from this recurrencia
              }
              return true; // keep real DB turnos
          }));
          return true;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return false;
      }
  };

  const updateTurno = async (turnoId: number, turnoData: Partial<Turno>, participantes: ParticipanteInput[]) => {
      if(readOnlyGuard()) return false;
      try {
          console.log('updateTurno: payload ->', { turnoId, turnoData, participantes });
          // 1. Actualizar tabla Turnos
          const { error: updateError } = await supabase
            .from('turnos')
            .update(turnoData)
            .eq('id', turnoId);

          if(updateError) throw updateError;

          // 2. Actualizar participantes (borrar y re-insertar)
          const { error: deletePartError } = await supabase.from('turno_participantes').delete().eq('turno_id', turnoId);
          if (deletePartError) throw deletePartError;

          const participantesData = buildParticipantesRows(turnoId, participantes);

          if(participantesData.length > 0) {
              const { data: partData, error: partError } = await supabase.from('turno_participantes').insert(participantesData).select();
              if(partError) throw partError;
              console.log('updateTurno: participantes insertados ->', partData);
          }

          // 3. Refrescar datos locales
          const { data: turnoCompleto } = await supabase
            .from('turnos')
            .select('*, cancha:canchas(*), socio_titular:socios(*), participantes:turno_participantes(*, socio:socios(*))')
            .eq('id', turnoId)
            .single();
          console.log('updateTurno: turnoCompleto ->', turnoCompleto);

          if(turnoCompleto) {
              const participantesFallback = participantesData.map(row => ({
                  turno_id: turnoId,
                  socio_id: row.socio_id,
                  nombre_libre: row.nombre_libre,
                  socio: row.socio_id ? socios.find(s => String(s.id) === String(row.socio_id)) : undefined
              }));

              const turnoFinal = {
                  ...(turnoCompleto as unknown as Turno),
                  participantes: (turnoCompleto as any).participantes && (turnoCompleto as any).participantes.length > 0
                      ? (turnoCompleto as any).participantes
                      : participantesFallback
              } as Turno;

              setTurnos(prev => prev.map(t => t.id === turnoId ? turnoFinal : t));
          }

          return true;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return false;
      }
  };

  // Actualiza un turno y si applyToFuture=true actualiza todas las ocurrencias futuras marcadas como [FIJO]
  const updateTurnoRecurrencia = async (turnoId: number, turnoData: Partial<Turno>, participantes: ParticipanteInput[], applyToFuture: boolean) => {
      if(readOnlyGuard()) return false;
      try {
          if (!applyToFuture) return await updateTurno(turnoId, turnoData, participantes);

          // obtener el turno original para conocer cancha, titular y fecha
          const original = turnos.find(t => t.id === turnoId);
          if (!original) return false;

          // 1) actualizar todos los turnos futuros que tengan la marca [FIJO]
          const { error: updateError } = await supabase
            .from('turnos')
            .update(turnoData)
            .eq('cancha_id', original.cancha_id)
            .eq('socio_titular_id', original.socio_titular_id)
            .ilike('notas', '%[FIJO]%')
            .gte('fecha_inicio', original.fecha_inicio);

          if (updateError) throw updateError;

          // 2) obtener ids afectados para sincronizar participantes
          const { data: affected } = await supabase
            .from('turnos')
            .select('id')
            .eq('cancha_id', original.cancha_id)
            .eq('socio_titular_id', original.socio_titular_id)
            .ilike('notas', '%[FIJO]%')
            .gte('fecha_inicio', original.fecha_inicio);

          if (affected && Array.isArray(affected) && affected.length > 0) {
              const ids = (affected as any[]).map(a => a.id);
              // borrar participantes antiguos y re-insertar los nuevos para cada turno
              const { error: deletePartsError } = await supabase.from('turno_participantes').delete().in('turno_id', ids);
              if (deletePartsError) throw deletePartsError;
              const participantesData: any[] = [];
              for (const tid of ids) {
                  participantesData.push(...buildParticipantesRows(tid, participantes));
              }
              if (participantesData.length > 0) {
                  const { error: partError } = await supabase.from('turno_participantes').insert(participantesData);
                  if (partError) throw partError;
              }
          }

          // 3) refrescar lista local de turnos
          const { data: nuevos } = await supabase
            .from('turnos')
            .select('*, cancha:canchas(*), socio_titular:socios(*), participantes:turno_participantes(*, socio:socios(*))')
            .eq('cancha_id', original.cancha_id)
            .eq('socio_titular_id', original.socio_titular_id)
            .ilike('notas', '%[FIJO]%')
            .gte('fecha_inicio', original.fecha_inicio)
            .order('fecha_inicio', { ascending: false });

          if (nuevos) {
              // eliminar antiguas ocurrencias de este grupo y reemplazar por las nuevas
              setTurnos(prev => prev.filter(t => !(t.cancha_id === original.cancha_id && t.socio_titular_id === original.socio_titular_id && String(t.notas || '').includes('[FIJO]') && t.fecha_inicio >= original.fecha_inicio)));
              setTurnos(prev => [...(nuevos as unknown as Turno[]), ...prev]);
          }

          return true;
      } catch (err: any) {
          setError(getErrorMessage(err));
          return false;
      }
  };

  const deleteTurno = async (turnoId: number) => {
      if(readOnlyGuard()) return false;
      
      if (user?.rol !== Rol.ADMIN) {
          ui.showModal('Permiso denegado', 'Solo administradores pueden eliminar turnos.');
          return false;
      }

      try {
          // Si el turno es fijo (marcado en notas con [FIJO]) eliminamos también las ocurrencias futuras
          const turno = turnos.find(t => t.id === turnoId);
          if (turno && turno.notas && String(turno.notas).includes('[FIJO]')) {
              const { error } = await supabase
                .from('turnos')
                .delete()
                .eq('cancha_id', turno.cancha_id)
                .eq('socio_titular_id', turno.socio_titular_id)
                .ilike('notas', '%[FIJO]%')
                .gte('fecha_inicio', turno.fecha_inicio);

              if (error) throw error;

              setTurnos(prev => prev.filter(t => !(t.cancha_id === turno.cancha_id && t.socio_titular_id === turno.socio_titular_id && String(t.notas || '').includes('[FIJO]') && t.fecha_inicio >= turno.fecha_inicio)));
              return true;
          }

          const { error } = await supabase.from('turnos').delete().eq('id', turnoId);
          if (error) throw error;

          setTurnos(prev => prev.filter(t => t.id !== turnoId));
          return true;
      } catch (err: any) {
           setError(getErrorMessage(err));
           return false;
      }
  };

  const updateTurnoEstado = async (turnoId: number, estado: Turno['estado']) => {
      const { error } = await supabase.from('turnos').update({ estado }).eq('id', turnoId);
      if(!error) {
          setTurnos(prev => prev.map(t => t.id === turnoId ? { ...t, estado } : t));
          return true;
      }
      return false;
  };

        const updateUserProfile = async (payload: { nombre?: string; email?: string; avatar_url?: string }): Promise<boolean> => {
            if (!user) return false;
            try {
                // If email present, update auth user email first
                if (payload.email && payload.email !== user.email) {
                    const { error: authErr } = await supabase.auth.updateUser({ email: payload.email });
                    if (authErr) throw authErr;
                }

                // Update profiles table with safe retry if avatar_url column is missing
                const toUpdate: any = {};
                if (payload.nombre !== undefined) toUpdate.nombre = payload.nombre;
                if (payload.avatar_url !== undefined) toUpdate.avatar_url = payload.avatar_url;

                if (Object.keys(toUpdate).length > 0) {
                    // Try updating normally
                    let res = await supabase.from('profiles').update(toUpdate).eq('id', user.id).select();
                    if (res.error) {
                        const msg = String(res.error.message || res.error || '');
                        // If error is because avatar_url column doesn't exist, retry without that field
                        if (msg.includes('avatar_url')) {
                            const safeUpdate = { ...toUpdate };
                            delete safeUpdate.avatar_url;
                            const retry = await supabase.from('profiles').update(safeUpdate).eq('id', user.id).select();
                            if (retry.error) throw retry.error;
                            res = retry;
                        } else {
                            throw res.error;
                        }
                    }

                    // Update local usuarios list
                    if (res.data && res.data.length > 0) {
                        setUsuarios(prev => prev.map(u => u.id === user.id ? { ...u, ...(res.data[0] as any) } : u));
                    }
                }

                // Update local user state
                setUser(prev => prev ? ({ ...prev, nombre: payload.nombre ?? prev.nombre, email: payload.email ?? prev.email, avatar_url: payload.avatar_url ?? prev.avatar_url }) : prev);

                return true;
            } catch (err: any) {
                setError(getErrorMessage(err));
                return false;
            }
        };

    // Build sanitized provider value - useMemo MUST be at component level, not inside render
    const sanitize = (v: any) => {
        if (v === null || v === undefined) return v;
        try { return JSON.parse(JSON.stringify(v)); } catch (e) { return v; }
    };
    const safeArr = (a: any) => Array.isArray(a) ? sanitize(a) : [];

    const providerValue = useMemo(() => ({
        user: sanitize(user),
        club: sanitize(club),
        supabase,
        loading,
        error,
        loginError,
        registrationError,
        isReadOnly,
        clearError,
        registerAndCreateClub,
        registerSocio,
        login,
        logout,
        fetchClubData,
        socios: safeArr(socios),
        addSocio,
        bulkAddSocios,
        updateSocio,
        deleteSocio,
        hardDeleteSocio,
        productos: safeArr(productos),
        addProducto,
        updateProducto,
        deleteProducto,
        sesionCaja: sanitize(sesionCaja),
        openCaja,
        closeCaja,
        registrarVenta,
        reglasPuntos: safeArr(reglasPuntos),
        addReglaPunto,
        updateReglaPunto,
        deleteReglaPunto,
        movimientosPuntos: safeArr(movimientosPuntos),
        deudas: safeArr(deudas),
        addDeudas,
        cobrarDeudas,
        cancelarDeuda,
        pendingOpenVentaId,
        openVentaInCaja,
        clearPendingOpenVentaId,
        pendingOpenTurnoId,
        openTurnoInTurnos,
        clearPendingOpenTurnoId,
        activeScreen,
        setActiveScreen,
        unauthenticatedView,
        setUnauthenticatedView,
        usuarios: safeArr(usuarios),
        createStaffUser,
        updateStaffUser,
        deleteStaffUser,
        clubes: safeArr(clubes),
        updateClubPlan,
        planes: safeArr(planes),
        addPlan,
        updatePlan,
        auditLogs: safeArr(auditLogs),
        facturas: safeArr(facturas),
        updateUserProfile,
        paymentSuccessMessage,
        setPaymentSuccessMessage,
        canchas: safeArr(canchas),
        addCancha,
        updateCancha,
        turnos: safeArr(turnos),
        addTurno,
        updateTurno,
        updateTurnoRecurrencia,
        addTurnoRecurrencia,
        updateTurnoRecurrenciaRecord,
        deleteTurnoRecurrencia,
        updateTurnoEstado,
        deleteTurno
    }), [
        user, club, loading, error, loginError, registrationError, isReadOnly,
        socios, productos, sesionCaja, reglasPuntos, movimientosPuntos, usuarios, clubes, planes, auditLogs, facturas, canchas, turnos,
        pendingOpenVentaId, pendingOpenTurnoId, activeScreen, unauthenticatedView, paymentSuccessMessage
    ]);

    return (
        <AppContext.Provider value={providerValue}>
            {children}
        </AppContext.Provider>
    );
};
